import { Alert, Button, ButtonGroup, Heading, Paragraph, TextButton } from '@zillow/constellation-10';
import { useIsFetching } from '@zg-rentals/react-query5';

import LeaseModal from './Containers/LeaseModal';
import type { Lease } from '../models';
import { CANCEL } from '../constants/stringConstants';
import { useLeaseWebApiHandler_deactivateLease } from '@zg-rentals/ts-leasing-client-react-query';

type Props = {
  lease: Lease;
  onClose(): void;
};

function DeactivateLeaseConfirmationModal({ lease, onClose }: Props): JSX.Element {
  const isFetching = useIsFetching() > 0;
  const deactivateLeaseMutation = useLeaseWebApiHandler_deactivateLease({
    meta: {
      invalidates: ['leasing:lease-dashboard-change'],
    },
  });
  const isApiLoading = isFetching || deactivateLeaseMutation.isPending;

  async function onDeactivateLease() {
    await deactivateLeaseMutation.mutateAsync({ getParams: { leaseId: lease.id } });
    onClose();
  }

  return (
    <LeaseModal
      id="discard-changes"
      isOpen
      onClose={onClose}
      header={<Heading level={1}>Deactivate your lease?</Heading>}
      body={
        <>
          {lease?.latestExtension && (
            <Alert tone="warning" css={{ marginBottom: 'tight' }}>
              By deactivating your lease, all lease renewal agreements will also be deactivated.
            </Alert>
          )}
          <Paragraph>
            By deactivating this lease in Zillow Rental Manager, you acknowledge that this lease is no longer in effect.
            Please note that this cancellation only applies within Zillow Rental Manager and does not terminate the
            actual lease agreement, nor does it provide legal notice of termination of the lease to your tenants.
          </Paragraph>
        </>
      }
      footer={
        <ButtonGroup aria-label="deactivate lease">
          <TextButton tone="brand" onClick={onClose} disabled={isApiLoading}>
            {CANCEL}
          </TextButton>
          <Button
            loading={isApiLoading}
            tone="brand"
            emphasis="filled"
            onClick={onDeactivateLease}
            disabled={isApiLoading}
          >
            Yes
          </Button>
        </ButtonGroup>
      }
    />
  );
}

export default DeactivateLeaseConfirmationModal;
